import type { Layout, PageCount, PageSlug, Section, SitePage } from './zod.js';

export const PACKAGE_OPTIONS: ReadonlyArray<{
  pageCount: PageCount;
  label: string;
  blurb: string;
}> = [
  { pageCount: 1, label: 'Landing', blurb: '1 page' },
  { pageCount: 3, label: 'Core 3', blurb: 'Home · Services · About' },
  { pageCount: 4, label: 'Core 4', blurb: 'Home · Services · About · FAQ' },
  { pageCount: 5, label: 'Core 5', blurb: 'Home · Services · About · FAQ · Work' },
];

const PAGE_TITLE: Record<PageSlug, string> = {
  '/': 'Home',
  '/services': 'Services',
  '/about': 'About',
  '/faq': 'FAQ',
  '/work': 'Work',
  '/start': 'Start',
  '/contact': 'Contact',
};

function unique(sections: readonly Section[]): Section[] {
  const seen = new Set<Section>();
  const out: Section[] = [];
  for (const s of sections) {
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function pick(pool: readonly Section[], wanted: readonly Section[]): Section[] {
  return wanted.filter((s) => pool.includes(s));
}

function withoutContact(sections: readonly Section[]): Section[] {
  return sections.filter((s) => s !== 'Contact');
}

function page(slug: PageSlug, sections: readonly Section[]): SitePage {
  return { slug, title: PAGE_TITLE[slug], sections: unique(sections) };
}

const FALLBACK_SECTIONS: Section[] = ['Hero', 'Services', 'ValueProps', 'FAQ'];

/**
 * Old DNA (no pageCount) is a single scroll. New leads set pageCount: 3.
 */
export function resolvedPageCount(layout: Pick<Layout, 'pageCount' | 'pages'>): PageCount {
  const n = layout.pageCount;
  if (n === 1 || n === 3 || n === 4 || n === 5) return n;
  return 1;
}

export function isMultiPage(layout: Pick<Layout, 'pageCount' | 'pages'>): boolean {
  return resolvedPageCount(layout) > 1 && (layout.pages?.length ?? 0) > 1;
}

export function pagesForPackage(
  pageCount: PageCount,
  industrySections: readonly Section[] = FALLBACK_SECTIONS,
): SitePage[] {
  const pool = withoutContact(industrySections.length ? unique(industrySections) : FALLBACK_SECTIONS);

  if (pageCount === 1) {
    return [page('/', pool)];
  }

  const homeProof = pick(pool, ['SocialProof', 'Testimonials']);
  const servicesSecs = pick(pool, ['Services', 'Pricing']);
  if (!servicesSecs.includes('Services')) servicesSecs.unshift('Services');
  const aboutSecs = pick(pool, ['ValueProps']);
  if (aboutSecs.length === 0) aboutSecs.push('ValueProps');

  const pages: SitePage[] = [
    page('/', ['Hero', ...homeProof]),
    page('/services', servicesSecs),
    page('/about', aboutSecs),
  ];

  if (pageCount >= 4) {
    pages.push(page('/faq', ['FAQ']));
  }

  if (pageCount >= 5) {
    pages.push(
      page('/work', unique(['Gallery', 'Testimonials', ...pick(pool, ['BeforeAfter'])])),
    );
  }

  return pages;
}

export function flattenPages(pages: readonly SitePage[]): Section[] {
  return unique(pages.flatMap((p) => p.sections));
}

export function applyPackageToLayout(
  industrySections: readonly Section[],
  pageCount: PageCount,
): Pick<Layout, 'pageCount' | 'pages' | 'sectionOrder'> {
  const pages = pagesForPackage(pageCount, industrySections);
  return { pageCount, pages, sectionOrder: flattenPages(pages) };
}

export function pageBySlug(
  pages: readonly SitePage[] | undefined,
  slug: PageSlug,
): SitePage | undefined {
  return pages?.find((p) => p.slug === slug);
}

export function hasPage(pages: readonly SitePage[] | undefined, slug: PageSlug): boolean {
  return Boolean(pageBySlug(pages, slug));
}

export function sitemapCrumbs(layout: Pick<Layout, 'pageCount' | 'pages'>): string {
  const pages = layout.pages ?? [];
  if (pages.length > 0) return pages.map((p) => p.title).join(' · ');
  return resolvedPageCount(layout) === 1
    ? 'Landing'
    : (PACKAGE_OPTIONS.find((o) => o.pageCount === layout.pageCount)?.blurb ?? '');
}

/** Progressive enhancement: JS intercepts to the inquire dialog. */
export function contactHref(_layout?: Pick<Layout, 'pageCount' | 'pages'>): string {
  return '/start';
}

export function servicesHref(layout: Pick<Layout, 'pageCount' | 'pages'>): string {
  return isMultiPage(layout) ? '/services' : '#services';
}

export function homeHref(layout: Pick<Layout, 'pageCount' | 'pages'>): string {
  return isMultiPage(layout) ? '/' : '#top';
}

export function aboutHref(layout: Pick<Layout, 'pageCount' | 'pages'>): string {
  return isMultiPage(layout) && hasPage(layout.pages, '/about') ? '/about' : '#value-props';
}

export function faqHref(layout: Pick<Layout, 'pageCount' | 'pages'>): string {
  return isMultiPage(layout) && hasPage(layout.pages, '/faq') ? '/faq' : '#faq';
}

export function workHref(layout: Pick<Layout, 'pageCount' | 'pages'>): string {
  return isMultiPage(layout) && hasPage(layout.pages, '/work') ? '/work' : '#gallery';
}

export interface NavItem {
  href: string;
  label: string;
}

/** Hash nav for landing; real paths for Core 3/4/5. */
export function navItemsForLayout(
  layout: Layout,
  hashNav: (sectionOrder: readonly Section[]) => NavItem[],
): NavItem[] {
  if (!isMultiPage(layout)) {
    return hashNav(layout.sectionOrder.length ? layout.sectionOrder : FALLBACK_SECTIONS);
  }
  return (layout.pages ?? []).map((p) => ({
    href: p.slug,
    label: p.title,
  }));
}
